/**
 * Integrations Dashboard
 * Main page for managing external system integrations
 */

import React, { useState, useEffect } from 'react';
import {
  Database,
  Plus,
  Activity,
  CheckCircle,
  AlertTriangle,
  RefreshCw,
  Upload,
  Languages,
  Building2,
  FileSpreadsheet,
  ArrowUpFromLine
} from 'lucide-react';
import {
  listIntegrations,
  getIntegrationTypeLabel,
  IntegrationConfig,
  IntegrationType
} from '@/services/api/integrations';
import { IntegrationConfigModal } from './IntegrationConfigModal';
import { IntegrationCard } from './IntegrationCard';
import { IntegrationExecutionMonitor } from './IntegrationExecutionMonitor';
import { CSVUploadWizard } from './CSVUploadWizard';
import { IntegrationDashboard } from './IntegrationDashboard';
import IntegrationWorkbenchSimple from './IntegrationWorkbenchSimple';
import MifImportWizard from './MifImportWizard';
import McpSubmissions from './McpSubmissions';
import { TranslationManager } from './TranslationManager';
import BenefitSystemsIntegration from './BenefitSystemsIntegration';

import { useAppTranslation } from '@/contexts/TranslationContext';
export const IntegrationsDashboard: React.FC = () => {
  const { t } = useAppTranslation();
  const [integrations, setIntegrations] = useState<IntegrationConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'mif' | 'submissions' | 'bulk_import' | 'benefit_systems' | 'all' | 'health_plan' | 'fhir' | 'crm' | 'housing' | 'csv_upload' | 'monitoring' | 'translations'>('bulk_import');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<IntegrationConfig | null>(null);
  const [showExecutionMonitor, setShowExecutionMonitor] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Read pre-selected entity type from sessionStorage (set by Contact Directory sync button)
  const [preselectedEntityType, setPreselectedEntityType] = useState<string | null>(null);

  useEffect(() => {
    const entityType = sessionStorage.getItem('integration_entity_type');
    if (entityType) {
      setPreselectedEntityType(entityType);
      sessionStorage.removeItem('integration_entity_type'); // Clear after reading
      setActiveTab('bulk_import'); // Switch to bulk import tab
    }
  }, []);

  // Fetch integrations
  const fetchIntegrations = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await listIntegrations();
      setIntegrations(data);
    } catch (err: any) {
      console.error('Error fetching integrations:', err);
      // Detect 405 errors (backend not deployed)
      if (err.response?.status === 405) {
        setError('Integration Workbench backend not yet deployed. Coming soon!');
      } else {
        setError(err.response?.data?.detail || 'Failed to load integrations');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIntegrations();
  }, []);

  // Filter integrations by type
  const filteredIntegrations = integrations.filter(integration => {
    if (activeTab === 'all') return true;
    return integration.integration_type === activeTab;
  });

  // Calculate summary stats
  const stats = {
    total: integrations.length,
    active: integrations.filter(i => i.is_active).length,
    healthPlan: integrations.filter(i => i.integration_type === 'health_plan').length,
    fhir: integrations.filter(i => i.integration_type === 'fhir').length,
    crm: integrations.filter(i => i.integration_type === 'crm').length,
    housing: integrations.filter(i => i.integration_type === 'housing').length,
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Toolbar */}
        <div className="mb-6">
          <div className="flex flex-wrap items-center justify-end gap-2 mb-4">
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
            >
              <Plus className="h-5 w-5" />
              <span className="hidden md:inline">{t('integrations.new_integration', 'New Integration')}</span>
            </button>
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <StatCard
              label={t('integrations.total', 'Total')}
              value={stats.total}
              icon={<Database className="h-5 w-5" />}
              color="blue"
            />
            <StatCard
              label={t('integrations.active', 'Active')}
              value={stats.active}
              icon={<CheckCircle className="h-5 w-5" />}
              color="green"
            />
            <StatCard
              label={t('integrations.health_plans', 'Health Plans')}
              value={stats.healthPlan}
              icon={<Activity className="h-5 w-5" />}
              color="purple"
            />
            <StatCard
              label="FHIR/EMR"
              value={stats.fhir}
              icon={<Activity className="h-5 w-5" />}
              color="indigo"
            />
            <StatCard
              label="CRM"
              value={stats.crm}
              icon={<Activity className="h-5 w-5" />}
              color="pink"
            />
            <StatCard
              label={t('integrations.housing', 'Housing')}
              value={stats.housing}
              icon={<Activity className="h-5 w-5" />}
              color="teal"
            />
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm mb-6">
          <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300">
            <div className="flex w-max min-w-full">
              <Tab
                label={t('integrations.health_plan_member_file', 'Health Plan Member File')}
                mobileLabel="MIF"
                active={activeTab === 'mif'}
                onClick={() => setActiveTab('mif')}
                icon={<FileSpreadsheet className="h-4 w-4" />}
              />
              <Tab
                label={t('integrations.mcp_submissions', 'Plan Submissions')}
                mobileLabel="Submit"
                active={activeTab === 'submissions'}
                onClick={() => setActiveTab('submissions')}
                icon={<ArrowUpFromLine className="h-4 w-4" />}
              />
              <Tab
                label={t('integrations.bulk_import_workbench', 'Bulk Import Workbench')}
                mobileLabel="Import"
                active={activeTab === 'bulk_import'}
                onClick={() => setActiveTab('bulk_import')}
                icon={<Database className="h-4 w-4" />}
              />
              <Tab
                label={t('integrations.benefit_systems', 'Benefit Systems')}
                mobileLabel="Benefits"
                active={activeTab === 'benefit_systems'}
                onClick={() => setActiveTab('benefit_systems')}
                icon={<Building2 className="h-4 w-4" />}
              />
              <Tab
                label={t('integrations.all_integrations', 'All Integrations')}
                mobileLabel="All"
                active={activeTab === 'all'}
                onClick={() => setActiveTab('all')}
                count={stats.total}
              />
              <Tab
                label={t('integrations.csvexcel_upload', 'CSV/Excel Upload')}
                mobileLabel="CSV"
                active={activeTab === 'csv_upload'}
                onClick={() => setActiveTab('csv_upload')}
                icon={<Upload className="h-4 w-4" />}
              />
              <Tab
                label={t('integrations.monitoring', 'Monitoring')}
                mobileLabel="Monitor"
                active={activeTab === 'monitoring'}
                onClick={() => setActiveTab('monitoring')}
                icon={<Activity className="h-4 w-4" />}
              />
              <Tab
                label={t('integrations.health_plans_1', 'Health Plans')}
                mobileLabel="Health"
                active={activeTab === 'health_plan'}
                onClick={() => setActiveTab('health_plan')}
                count={stats.healthPlan}
              />
              <Tab
                label="FHIR / EMR"
                mobileLabel="FHIR"
                active={activeTab === 'fhir'}
                onClick={() => setActiveTab('fhir')}
                count={stats.fhir}
              />
              <Tab
                label="CRM"
                active={activeTab === 'crm'}
                onClick={() => setActiveTab('crm')}
                count={stats.crm}
              />
              <Tab
                label={t('integrations.housing_1', 'Housing')}
                active={activeTab === 'housing'}
                onClick={() => setActiveTab('housing')}
                count={stats.housing}
              />
              <Tab
                label={t('integrations.translations', 'Translations')}
                mobileLabel="Trans"
                active={activeTab === 'translations'}
                onClick={() => setActiveTab('translations')}
                icon={<Languages className="h-4 w-4" />}
              />
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className={`border rounded-lg p-4 mb-6 flex items-center gap-3 ${
            error.includes('not yet deployed') || error.includes('Coming soon')
              ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
              : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
          }`}>
            <AlertTriangle className={`h-5 w-5 flex-shrink-0 ${
              error.includes('not yet deployed') || error.includes('Coming soon')
                ? 'text-blue-600'
                : 'text-red-600'
            }`} />
            <div className="flex-1">
              <p className={`font-medium ${
                error.includes('not yet deployed') || error.includes('Coming soon')
                  ? 'text-blue-800 dark:text-blue-200'
                  : 'text-red-800 dark:text-red-200'
              }`}>
                {error.includes('not yet deployed') || error.includes('Coming soon')
                  ? 'Integration Workbench'
                  : 'Error loading integrations'}
              </p>
              <p className={`text-sm ${
                error.includes('not yet deployed') || error.includes('Coming soon')
                  ? 'text-blue-600 dark:text-blue-400'
                  : 'text-red-600 dark:text-red-400'
              }`}>{error}</p>
              {(error.includes('not yet deployed') || error.includes('Coming soon')) && (
                <p className="text-xs text-blue-500 dark:text-blue-400 mt-2">
                  {t('integrations.the_mcpbased_integration_framework_is', 'The MCP-based integration framework is being deployed. Check back soon!')}
                </p>
              )}
            </div>
            <button
              onClick={fetchIntegrations}
              className={`flex items-center gap-2 ${
                error.includes('not yet deployed') || error.includes('Coming soon')
                  ? 'text-blue-600 hover:text-blue-700'
                  : 'text-red-600 hover:text-red-700'
              }`}
            >
              <RefreshCw className="h-4 w-4" />
              {t('integrations.retry', 'Retry')}
            </button>
          </div>
        )}

        {/* Content */}
        {activeTab === 'mif' ? (
          <MifImportWizard />
        ) : activeTab === 'submissions' ? (
          <McpSubmissions />
        ) : activeTab === 'bulk_import' ? (
          <IntegrationWorkbenchSimple defaultEntityType={preselectedEntityType || undefined} />
        ) : activeTab === 'benefit_systems' ? (
          <BenefitSystemsIntegration />
        ) : activeTab === 'csv_upload' ? (
          <CSVUploadWizard />
        ) : activeTab === 'monitoring' ? (
          <IntegrationDashboard />
        ) : activeTab === 'translations' ? (
          <TranslationManager />
        ) : loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
              <p className="text-gray-600 dark:text-gray-400">{t('integrations.loading_integrations', 'Loading integrations...')}</p>
            </div>
          </div>
        ) : filteredIntegrations.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-12 text-center">
            <Database className="h-16 w-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">{t('integrations.no_integrations_found', 'No integrations found')}</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {activeTab === 'all'
                ? 'Get started by creating your first integration'
                : `No ${getIntegrationTypeLabel(activeTab as IntegrationType)} integrations configured`}
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
            >
              <Plus className="h-5 w-5" />
              {t('integrations.create_integration', 'Create Integration')}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredIntegrations.map(integration => (
              <IntegrationCard
                key={integration.id}
                integration={integration}
                onEdit={(integration) => {
                  setSelectedIntegration(integration);
                  setShowCreateModal(true);
                }}
                onExecute={(integration) => {
                  setSelectedIntegration(integration);
                  setShowExecutionMonitor(true);
                }}
                onRefresh={fetchIntegrations}
              />
            ))}
          </div>
        )}

        {/* Create/Edit Modal */}
        {showCreateModal && (
          <IntegrationConfigModal
            integration={selectedIntegration}
            onClose={() => {
              setShowCreateModal(false);
              setSelectedIntegration(null);
            }}
            onSuccess={() => {
              setShowCreateModal(false);
              setSelectedIntegration(null);
              fetchIntegrations();
            }}
          />
        )}

        {/* Execution Monitor Modal */}
        {showExecutionMonitor && selectedIntegration && (
          <IntegrationExecutionMonitor
            integration={selectedIntegration}
            onClose={() => {
              setShowExecutionMonitor(false);
              setSelectedIntegration(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

// Stat Card Component
interface StatCardProps {
  label: string;
  value: number;
  icon: React.ReactNode;
  color: 'blue' | 'green' | 'purple' | 'indigo' | 'pink' | 'teal';
}

const StatCard: React.FC<StatCardProps> = ({ label, value, icon, color }) => {
  const colorClasses = {
    blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
    green: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
    purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
    indigo: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400',
    pink: 'bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400',
    teal: 'bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400',
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          {icon}
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</p>
          <p className="text-xs text-gray-600 dark:text-gray-400">{label}</p>
        </div>
      </div>
    </div>
  );
};

// Tab Component
interface TabProps {
  label: string;
  mobileLabel?: string;
  active: boolean;
  onClick: () => void;
  count?: number;
  icon?: React.ReactNode;
}

const Tab: React.FC<TabProps> = ({ label, mobileLabel, active, onClick, count, icon }) => (
  <button
    onClick={onClick}
    className={`
      px-3 sm:px-6 py-3 font-medium text-xs sm:text-sm whitespace-nowrap flex items-center gap-1 sm:gap-2 flex-shrink-0
      ${active
        ? 'text-teal-600 border-b-2 border-teal-600 bg-teal-50 dark:bg-teal-900/20'
        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700'
      }
    `}
  >
    {icon}
    {mobileLabel ? (
      <>
        <span className="hidden sm:inline">{label}</span>
        <span className="sm:hidden">{mobileLabel}</span>
      </>
    ) : (
      label
    )}
    {count !== undefined && (
      <span className={`
        px-1.5 sm:px-2 py-0.5 rounded-full text-xs
        ${active ? 'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'}
      `}>
        {count}
      </span>
    )}
  </button>
);
